import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API, withCreds, fmtDate } from '../../components/admin/AdminUI';
import { describeLine } from '../../lib/cartLine';

// Plain, printer-friendly page (no admin chrome). Opens the print dialog once loaded.
export default function PackingSlip() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [from, setFrom] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/orders/${id}`, withCreds),
      axios.get(`${API}/admin/store-settings`, withCreds),
    ]).then(([o, s]) => {
      setOrder(o.data);
      setFrom(s.data.ship_from);
      setTimeout(() => window.print(), 300);
    }).catch(() => setError('Could not load this order. Are you signed in as an admin?'));
  }, [id]);

  if (error) return <p style={{ padding: 32, fontFamily: 'sans-serif' }}>{error}</p>;
  if (!order) return <p style={{ padding: 32, fontFamily: 'sans-serif' }}>Loading…</p>;

  const a = order.shipping_address || {};
  const units = order.items.reduce((n, i) => n + i.quantity, 0);

  return (
    <div className="min-h-screen bg-white text-black p-10 print:p-0" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
      <style>{'@page { margin: 0.5in; } @media print { .no-print { display: none !important; } }'}</style>
      <div className="max-w-2xl mx-auto">
        <div className="no-print mb-6 flex gap-2">
          <button onClick={() => window.print()} className="px-4 py-2 bg-black text-white rounded">Print</button>
          <button onClick={() => window.close()} className="px-4 py-2 border border-gray-300 rounded">Close</button>
        </div>

        <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">{from?.company || 'Market @ Buzzzillion'}</h1>
            <p className="text-sm text-gray-600 leading-snug mt-1">
              {from?.street1}{from?.street2 ? `, ${from.street2}` : ''}<br />
              {from?.city}{from?.city ? ', ' : ''}{from?.state} {from?.zip}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-gray-500">Packing slip</p>
            <p className="text-lg font-bold font-mono">{order.order_number}</p>
            <p className="text-sm text-gray-600">{fmtDate(order.created_at)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Ship to</p>
            <p className="leading-snug">
              <b>{a.name}</b><br />{a.line1}{a.line2 ? <><br />{a.line2}</> : null}<br />
              {a.city}, {a.state} {a.postal_code}<br />{a.country}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Customer</p>
            <p className="leading-snug">{order.customer_name}<br />{order.customer_email}</p>
            {order.tracking_number && (
              <>
                <p className="text-xs uppercase tracking-widest text-gray-500 mt-3 mb-1">Tracking</p>
                <p className="font-mono text-sm">{order.carrier} {order.tracking_number}</p>
              </>
            )}
          </div>
        </div>

        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-black text-left">
              <th className="py-2 w-10">✓</th>
              <th className="py-2">Item</th>
              <th className="py-2">SKU</th>
              <th className="py-2 text-right">Qty</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((item, i) => (
              <tr key={i} className="border-b border-gray-300">
                <td className="py-3"><span className="inline-block w-4 h-4 border border-black" /></td>
                <td className="py-3">{item.title}{describeLine(item) && <div className="text-xs text-gray-700 mt-0.5">{describeLine(item)}</div>}
                  {item.bundle_items?.length > 0 && (
                    <ul className="text-xs text-gray-700 mt-1 list-disc ml-4">
                      {item.bundle_items.map(b => <li key={b.product_id}>{b.quantity * item.quantity} × {b.title}{b.sku ? ` (${b.sku})` : ''}</li>)}
                    </ul>
                  )}</td>
                <td className="py-3 font-mono">{item.sku || '—'}</td>
                <td className="py-3 text-right font-bold">{item.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-right text-sm mt-3"><b>{units}</b> unit{units === 1 ? '' : 's'} total</p>

        <p className="text-center text-sm text-gray-600 mt-16">Thank you for your order!</p>
      </div>
    </div>
  );
}
